import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { MessageCircle } from "lucide-react";
import { PA_BASE, IMAGES, img, waLink } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline } from "../components/motion";

const PILLARS = [
  {
    n: "01",
    title: "Executive & board travel",
    body: "Point-to-point charter built around board calendars — with schedule flexibility, private terminals and handling that respects seniority and time.",
  },
  {
    n: "02",
    title: "Roadshows & multi-stop",
    body: "Multi-city investor roadshows and site visits sequenced as a single itinerary — one operator brief, one point of contact, no airline connections.",
  },
  {
    n: "03",
    title: "Delegation & group charter",
    body: "Larger parties moved together — from heavy jets to regional airliners sourced through our network for events, incentives and project crew.",
  },
  {
    n: "04",
    title: "Confidentiality",
    body: "Manifests, movements and passenger details handled discreetly and shared only with the parties required to operate the flight.",
  },
];

export default function CorporatePage() {
  return (
    <>
      <Seo
        title="Corporate Aviation — Business Charter Without Ownership | JS Private Aviation"
        description="Charter access for companies that fly privately without owning an aircraft — executive travel, roadshows and group charter, sourced per trip."
      />
      <KineticHero
        testId="corporate-hero"
        fullHeight={false}
        overline="Corporate Aviation"
        lines={["Business aviation,", "without the overhead."]}
        sub="Charter access for companies that fly privately without owning an aircraft — sourced per trip through independent operators, with no fleet commitments."
        image={img(IMAGES.window, 2000)}
      >
        <Link
          to={`${PA_BASE}/charter#request`}
          data-testid="corporate-quote-cta"
          className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
        >
          Request a Corporate Quote
        </Link>
        <a
          href={waLink("Hello — I would like to discuss corporate charter for our company.")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="corporate-whatsapp-cta"
          className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <MessageCircle size={15} /> WhatsApp a Specialist
        </a>
      </KineticHero>

      <section className="bg-[#F9F9F6] py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <Overline>For companies</Overline>
            <h2 className="mt-3 max-w-2xl font-display text-3xl sm:text-4xl tracking-tight">
              The case for charter over ownership.
            </h2>
            <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-black/60">
              Ownership and jet cards tie capital to a single aircraft or program. Charter through a
              brokerage does the opposite — the right aircraft for each mission, paid for only when you
              fly, with no positioning of someone else's fleet economics onto your balance sheet.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.05 * i} className="bg-[#F9F9F6]">
                <div data-testid={`corporate-pillar-${p.n}`} className="h-full p-10 sm:p-12">
                  <span className="font-mono text-sm text-black/30">{p.n}</span>
                  <h3 className="mt-4 font-display text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-black/55">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 text-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-8 px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Set up a corporate charter relationship.
            </h2>
            <p className="mt-4 max-w-lg text-[15px] text-white/55">
              One conversation with our charter team is enough to understand your travel pattern.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to={`${PA_BASE}/contact`}
              data-testid="corporate-contact-cta"
              className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
            >
              Contact us
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
